import {
  SETTINGS_PATHS,
  supplierInquiriesTabPath,
} from "@/features/settings/paths"

/** Single source of truth for the supplier-inquiry flow's route paths. */
export const INQUIRY_BASE = "/material-compliance/supplier-studio/inquiry"

export const inquiryNewPath = `${INQUIRY_BASE}/new`

/**
 * The draft composer for one or more inquiries. The composer takes its ids from
 * the query string (repeated `ids` for a batch), which is why an inquiry cannot
 * be reached through an `AgentTarget.route` — those never carry a query.
 */
export function inquiryDraftPath(
  inquiryIds: string | string[],
  stage?: "reminder",
): string {
  const ids = Array.isArray(inquiryIds) ? inquiryIds : [inquiryIds]
  const params = new URLSearchParams()
  for (const id of ids) {
    params.append("ids", id)
  }
  if (stage) {
    params.set("stage", stage)
  }
  return `${INQUIRY_BASE}/draft?${params.toString()}`
}

/**
 * One inquiry selected in the list, which is what renders its detail panel and the
 * full email thread. `?id=` is the list's own selection param — InquiryListPanel
 * and InquiryDetailPanel both read it — so this is a real deep link, not a
 * chat-only route.
 */
export function inquiryDetailPath(inquiryId: string): string {
  return `${INQUIRY_BASE}?${new URLSearchParams({ id: inquiryId })}`
}

/**
 * Where each section of the former inquiry template-settings dialog lives now.
 * The dialog opened from `?settings=<section>` on the inquiry list; links
 * carrying that (bookmarks, earlier agent-chat messages) are sent on here.
 */
const SETTINGS_PATH_BY_LEGACY_SECTION: Record<string, string> = {
  "organization-email": SETTINGS_PATHS.emailSending,
  logo: SETTINGS_PATHS.general,
  attachment: supplierInquiriesTabPath("attachment"),
  // Held the default Cc and the signature; the Emails tab has the Cc and
  // links on to the signature.
  "email-configuration": supplierInquiriesTabPath("emails"),
  "email-template": supplierInquiriesTabPath("emails"),
  cadence: supplierInquiriesTabPath("deadlines"),
  declarations: SETTINGS_PATHS.conformityDeclarations,
  "master-data": SETTINGS_PATHS.masterData,
}

/** The settings page for a `?settings=` section of the old dialog. */
export function legacyInquirySettingsPath(section: string): string {
  return (
    SETTINGS_PATH_BY_LEGACY_SECTION[section] ?? SETTINGS_PATHS.supplierInquiries
  )
}
